import type { WebBotProviderData } from '../../../API.ts';
import { WidgetButtonShape } from '../../../API.ts';

export function calculateOptimalPreviewSize(data: Partial<WebBotProviderData>): { width: number; height: number } {
  const isValidNum = (v: any): v is number =>
    v !== null && v !== undefined && v !== '' && !isNaN(Number(v));

  const layout = data.layout || (data as any);

  const rawWinWidth = layout?.windowWidth ?? (data as any).widgetWindowWidth;
  const winWidth = isValidNum(rawWinWidth) ? Number(rawWinWidth) : 350;

  const rawWinHeight = layout?.windowHeight ?? (data as any).widgetWindowHeight;
  const winHeight = isValidNum(rawWinHeight) ? Number(rawWinHeight) : 500;

  const rawPosRight = layout?.positionRight ?? (data as any).positionRight;
  const posRight = isValidNum(rawPosRight) ? Number(rawPosRight) : 20;

  const rawPosLeft = layout?.positionLeft ?? (data as any).positionLeft;
  const posLeft = isValidNum(rawPosLeft) ? Number(rawPosLeft) : null;

  const rawPosBottom = layout?.positionBottom ?? (data as any).positionBottom;
  const posBottom = isValidNum(rawPosBottom) ? Number(rawPosBottom) : 20;

  const rawPosTop = layout?.positionTop ?? (data as any).positionTop;
  const posTop = isValidNum(rawPosTop) ? Number(rawPosTop) : null;

  const rawOffsetX = layout?.windowOffsetX ?? (data as any).widgetWindowOffsetX;
  const offsetX = isValidNum(rawOffsetX) ? Number(rawOffsetX) : 0;

  const rawOffsetY = layout?.windowOffsetY ?? (data as any).widgetWindowOffsetY;
  const offsetY = isValidNum(rawOffsetY) ? Number(rawOffsetY) : 0;

  const rawShape = layout?.buttonShape ?? (data as any).widgetButtonShape;
  const shape = rawShape ?? WidgetButtonShape.Circle;
  const isHideButton = String(shape).toLowerCase() === 'hide';

  const rawBtnHeight = layout?.buttonHeight ?? (data as any).widgetButtonHeight;
  const btnHeight = isValidNum(rawBtnHeight) ? Number(rawBtnHeight) : 60;
  const buttonVerticalSpace = isHideButton ? 0 : btnHeight + 10;

  let maxLeftExtWidth = 0;
  let maxRightExtWidth = 0;
  let maxTopExtHeight = 0;
  let maxBottomExtHeight = 0;

  if (Array.isArray(data.extensions)) {
    for (const ext of data.extensions) {
      const std = ext?.standard;
      if (std && std.enabled !== false) {
        const pos = (std.position || 'left').toLowerCase();
        const rawW = std.width ? parseInt(String(std.width), 10) : 0;
        const rawH = std.height ? parseInt(String(std.height), 10) : 0;

        const effectiveW = !isNaN(rawW) && rawW > 0 ? rawW : (pos === 'left' || pos === 'right' ? 220 : 0);
        const effectiveH = !isNaN(rawH) && rawH > 0 ? rawH : (pos === 'top' || pos === 'bottom' ? 160 : 0);

        if (pos === 'left') {
          maxLeftExtWidth = Math.max(maxLeftExtWidth, effectiveW);
        } else if (pos === 'right') {
          maxRightExtWidth = Math.max(maxRightExtWidth, effectiveW);
        } else if (pos === 'top') {
          maxTopExtHeight = Math.max(maxTopExtHeight, effectiveH);
        } else if (pos === 'bottom') {
          maxBottomExtHeight = Math.max(maxBottomExtHeight, effectiveH);
        }
      }
    }
  }

  const extensionGap = 8;
  const totalExtensionsWidth =
    (maxLeftExtWidth > 0 ? maxLeftExtWidth + extensionGap : 0) +
    (maxRightExtWidth > 0 ? maxRightExtWidth + extensionGap : 0);

  const totalExtensionsHeight =
    (maxTopExtHeight > 0 ? maxTopExtHeight + extensionGap : 0) +
    (maxBottomExtHeight > 0 ? maxBottomExtHeight + extensionGap : 0);

  const wrapperWidth = winWidth + totalExtensionsWidth;
  const wrapperHeight = winHeight + totalExtensionsHeight;

  const marginH =
    posLeft != null && posRight != null
      ? posLeft + posRight
      : (posLeft != null ? posLeft + 40 : posRight + 40);
  const totalOffsetH = Math.abs(offsetX);

  const marginV =
    posTop != null && posBottom != null
      ? posTop + posBottom
      : (posTop != null ? posTop + 40 : posBottom + 40);
  const totalOffsetV = Math.abs(offsetY);

  const computedWidth = wrapperWidth + marginH + totalOffsetH;
  const computedHeight = wrapperHeight + buttonVerticalSpace + marginV + totalOffsetV;

  return {
    width: Math.max(420, Math.ceil(computedWidth / 10) * 10),
    height: Math.max(580, Math.ceil(computedHeight / 10) * 10),
  };
}
